import { FC, UIEventHandler, useEffect, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List, VariableSizeList } from 'react-window';
import cn from 'classnames';

import { useIwBreakPoints } from '@/_pages/interwill/hooks/useIwBreakPoints';
import { throttle } from '@/shared/utils/timers';

import { Loader } from '../Loader';
import { PageItem } from '../PageItem';
import { DocumentWrapper, Pages, Sidebar, ThumbnailList } from '../styled';
import { ThumbnailItem } from '../ThumbnailItem';
import { FilePreviewData, PageViewport } from '../types';

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.min.js',
    import.meta.url,
  ).toString();
}

type PdfRendererProps = {
  fileData: FilePreviewData;
  containerWidth: number;
  thumbnailWidth: number;
  headerHidden: boolean;
  setHeaderHidden: (b: boolean) => void;
};

const PAGES_GAP = 24;
const PAGE_WIDTH = 895;

export const PdfRenderer: FC<PdfRendererProps> = ({
  fileData,
  containerWidth,
  thumbnailWidth,
  headerHidden,
  setHeaderHidden,
}) => {
  const [isPageRendered, setIsPageRendered] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [pdf, setPdf] = useState<DocumentCallback | null>(null);
  const [pageViewports, setPageViewports] = useState<PageViewport[] | null>(null);

  const list = useRef<HTMLDivElement>(null);
  const sidebarList = useRef<HTMLDivElement>(null);
  const pagesList = useRef<VariableSizeList>(null);
  const thumbnailsList = useRef<VariableSizeList>(null);

  const { isMobile } = useIwBreakPoints();

  const pageWidth = containerWidth < PAGE_WIDTH ? containerWidth : PAGE_WIDTH;

  const getPageHeight = (index: number) => {
    if (!pageViewports) return 0;
    const pageViewport = pageViewports[index];
    const scale = (containerWidth < PAGE_WIDTH ? containerWidth : PAGE_WIDTH) / pageViewport.width;
    const actualHeight = pageViewport.height * scale;

    return actualHeight + PAGES_GAP;
  };

  const pageHeight = pageViewports ? getPageHeight(activePage) : 0;

  const thumbnailHeight = (pageHeight / PAGE_WIDTH) * thumbnailWidth + 48;

  useEffect(() => {
    setPageViewports(null);

    const fetchPageViewports = async () => {
      if (!pdf) return;
      const pageNumbers = Array.from(new Array(pdf.numPages)).map((_, index) => index + 1);

      const pagePromises = pageNumbers.map(async (pageNumber) => {
        const page = await pdf.getPage(pageNumber);
        return page.getViewport({ scale: 1 });
      });

      const nextPageViewports = await Promise.all(pagePromises);

      setPageViewports(nextPageViewports);
    };

    if (pdf) {
      fetchPageViewports();
    }
  }, [pdf]);

  useEffect(() => {
    if (!numPages || !sidebarList.current) return;
    sidebarList.current.scrollTo({
      behavior: 'smooth',
      top: thumbnailHeight * activePage,
    });
  }, [activePage, numPages, sidebarList]);

  useEffect(() => {
    if (pagesList.current) {
      pagesList.current.resetAfterIndex(0, true);
    }

    if (thumbnailsList.current) {
      thumbnailsList.current.resetAfterIndex(0, true);
    }
  }, [containerWidth, pageViewports]);

  const onDocumentLoadSuccess = (entry: DocumentCallback) => {
    setNumPages(entry.numPages);
    setPdf(entry);
  };

  const onRenderSuccess = () => {
    setIsPageRendered(true);
  };

  const onScroll: UIEventHandler<HTMLDivElement> = throttle((e) => {
    const dy = e.currentTarget.scrollTop - lastScrollY;
    if (Math.abs(dy) < 100) return;
    setHeaderHidden(dy > 0);
    setLastScrollY(e.currentTarget.scrollTop);
  }, 250);

  const fileDocument = fileData.mode === 'file' ? fileData.file : fileData.fileInfo;

  return (
    <DocumentWrapper
      $hidden={headerHidden}
      file={fileDocument}
      loading={<Loader />}
      onClick={() => setShowSidebar(!showSidebar)}
      onLoadSuccess={onDocumentLoadSuccess}
      onItemClick={({ pageIndex }) => {
        if (list.current) {
          list.current.scrollTo({
            top: pageIndex * pageHeight,
            behavior: 'smooth',
          });
        }
      }}
    >
      {!isMobile && (
        <Sidebar className={cn({ rendered: isPageRendered })}>
          {pdf && pageViewports && (
            <AutoSizer disableWidth>
              {({ height }) => (
                <ThumbnailList
                  ref={thumbnailsList}
                  outerRef={sidebarList}
                  width={thumbnailWidth}
                  height={height}
                  estimatedItemSize={thumbnailHeight}
                  itemCount={pdf.numPages}
                  itemSize={() => thumbnailHeight}
                  itemData={{
                    activePage,
                    thumbnailWidth,
                    setActivePage,
                  }}
                >
                  {ThumbnailItem}
                </ThumbnailList>
              )}
            </AutoSizer>
          )}
        </Sidebar>
      )}
      {!isPageRendered && <Loader />}
      <Pages $rendered={isPageRendered} onScroll={onScroll}>
        {pdf && pageViewports && (
          <AutoSizer disableWidth>
            {({ height }) => (
              <List
                ref={pagesList}
                outerRef={list}
                width={pageWidth}
                height={height}
                estimatedItemSize={pageHeight}
                itemCount={pdf.numPages}
                itemSize={getPageHeight}
                itemData={{
                  width: pageWidth,
                  height: pageHeight,
                  setActivePage,
                  onRenderSuccess,
                }}
              >
                {PageItem}
              </List>
            )}
          </AutoSizer>
        )}
      </Pages>
    </DocumentWrapper>
  );
};
